import { css } from '@emotion/react'
import type { CasAssignmentDtoT } from '@mathflat/domain/@entities/(Content)/Cas'
import { useMemo } from 'react'

import { colors, typo } from '~/@common/styles'
import Button from '~/@common/ui/(Button)/Button/Button'
import 자동채점입력, { UNKNOWN_VALUE } from '~/@pages/@common/(Scoring)/(자동채점)/자동채점입력'

type Detail = CasAssignmentDtoT.AssignedCasDetail
type Problem = CasAssignmentDtoT.CasProblemDetail

type Props = {
  assignment: Detail
  problem: Problem | null
  currentInput: string
  isSubmitting: boolean
  isLastProblem: boolean
  onChangeInput: (next: string) => void
  onSubmit: () => void
  onUnknown: () => void
}

export const CasSolveViewMobile = ({
  assignment,
  problem,
  currentInput,
  isSubmitting,
  isLastProblem,
  onChangeInput,
  onSubmit,
  onUnknown,
}: Props) => {
  const total = assignment.maxProblemCount ?? 0
  const scored = assignment.scoredCount ?? 0
  const currentNumber = scored + 1
  const submitLabel = isLastProblem ? '제출하기' : '제출 후 다음문제'

  const isChoice =
    !!problem &&
    (problem.type === 'SINGLE_CHOICE' ||
      problem.type === 'MULTIPLE_CHOICE' ||
      (problem.optionCount ?? 0) > 0)
  const isUnknownSelected = currentInput === UNKNOWN_VALUE
  const hasAnswer = !!currentInput

  const handleSubmit = () => {
    if (isUnknownSelected) {
      onUnknown()
      return
    }
    onSubmit()
  }

  const choiceData = useMemo(
    () =>
      problem
        ? Array.from({ length: problem.optionCount }, (_, idx) => ({
            label: String(idx + 1),
            value: String(idx + 1),
          }))
        : [],
    [problem],
  )

  return (
    <div css={S.page}>
      <div css={S.progress}>
        <span css={S.progressNumber}>{currentNumber}번 문제</span>
        <span css={S.progressTotal}> / 총 {total}문제</span>
      </div>

      {problem ? (
        <>
          <section css={S.card}>
            <header css={S.cardHeader}>
              <div css={S.cardHeaderNumber}>{currentNumber}번</div>
              <div css={S.cardHeaderStatus}>
                <CheckIcon />
                <span>채점 후 정답・해설이 공개됩니다.</span>
              </div>
            </header>
            <div css={S.cardBody}>
              <img src={problem.problemImageUrl} alt={`문제 ${currentNumber}`} />
            </div>
            <div css={S.cardFooter}>
              {isChoice ? (
                <자동채점입력.객관식
                  max={problem.answerCount || 1}
                  values={currentInput ? currentInput.split(',') : []}
                  onChange={(next) => onChangeInput(next.map((v) => String(v)).join(','))}
                  data={choiceData}
                />
              ) : (
                <자동채점입력.주관식
                  id={problem.id}
                  value={currentInput}
                  onChange={(next) => onChangeInput(next ?? '')}
                  keypadTypes={problem.keypadTypes}
                  schoolType={assignment.schoolType}
                  answerUnits={problem.answerUnits}
                  virtualKeybaordShowType="fixed"
                />
              )}
            </div>
          </section>

          <div css={S.submitArea}>
            <Button
              theme="primary"
              size="large"
              style={{ width: '100%' }}
              onClick={handleSubmit}
              disabled={!hasAnswer || isSubmitting}
            >
              {submitLabel}
            </Button>
          </div>
        </>
      ) : (
        <div css={S.empty}>현재 풀 문항이 없어요.</div>
      )}
    </div>
  )
}

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      d="M4 10.5L8 14.5L16 6.5"
      stroke={'var(--Colors-Gray-500)'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const S = {
  page: css`
    display: flex;
    flex-direction: column;
    gap: 20px;
    height: 100%;
    width: 100%;
    padding-bottom: 16px;
    background: #f0f2f4;
  `,
  progress: css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 48px;
    padding: 10px 16px;
    margin-top: 20px;
  `,
  progressNumber: css`
    ${typo.body02};
    font-weight: 700;
    color: var(--Colors-Gray-900);
  `,
  progressTotal: css`
    ${typo.body02};
    color: var(--Colors-Gray-900);
  `,
  card: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    margin: 0 16px;
    border-radius: var(--Radius-300);
    background: var(--White);
    overflow: hidden;
    filter: drop-shadow(0 4px 10px rgba(0, 0, 0, 0.05));
  `,
  cardHeader: css`
    display: flex;
    align-items: stretch;
    min-height: 70px;
    border-bottom: 1px solid var(--Colors-Gray-300);
  `,
  cardHeaderNumber: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    flex-shrink: 0;
    background: var(--Colors-Gray-100);
    ${typo.body02};
    font-weight: 700;
    color: var(--Colors-Gray-800);
  `,
  cardHeaderStatus: css`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 20px;
    ${typo.body02};
    color: var(--Colors-Gray-500);
  `,
  cardBody: css`
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 20px;
    overflow: auto;
    border-bottom: 1px solid var(--Colors-Gray-300);
    img {
      max-width: 100%;
      height: auto;
    }
  `,
  cardFooter: css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px 22px;
  `,
  submitArea: css`
    padding: 0 16px;
  `,
  empty: css`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--Colors-Gray-500);
    ${typo.body02};
  `,
}
